// ============================================
// TRADE ADVISOR - AI-Powered Trade Management
// Using 7-Step Framework with Perplexity
// ============================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useUserData } from '../contexts/UserContext';
import {
  MessageSquare,
  Upload,
  Send,
  Image as ImageIcon,
  X,
  Loader2,
  AlertCircle,
  Lock,
  Sparkles,
  Target,
  TrendingUp,
  Shield,
  Clock,
  ChevronDown,
  ChevronUp,
  Trash2
} from 'lucide-react';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  imageUrl?: string;
  timestamp: Date;
  citations?: string[];
}

const EXAMPLE_PROMPTS = [
  "Evaluate my position using the 7-step framework",
  "I hit my first target. What's next?",
  "Stock broke support. Should I exit?",
  "Earnings are in 3 days. How should I manage this?",
  "Create a decision table for my position",
  "Am I being too tight/loose with my trailing stop?",
  "Should I hold over the weekend?",
  "What's my risk:reward ratio?"
];

export const TradeAdvisorPage: React.FC = () => {
  const { user, isSignedIn, isLoaded } = useUser();
  const { userData, loading: userLoading } = useUserData();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showExamples, setShowExamples] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB');
      return;
    }

    // Convert to base64 for preview and sending
    const reader = new FileReader();
    reader.onload = (event) => {
      setImagePreview(event.target?.result as string);
      setError(null);
    };
    reader.readAsDataURL(file);
  }, []);

  const clearImage = useCallback(() => {
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const sendMessage = useCallback(async () => {
    if ((!input.trim() && !imagePreview) || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim() || 'Please analyze this position screenshot.',
      imageUrl: imagePreview || undefined,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    clearImage();
    setIsLoading(true);
    setError(null);
    setShowExamples(false);

    try {
      // Build messages for API
      const apiMessages = [...messages, userMessage].map(m => ({
        role: m.role,
        content: m.content,
        imageUrl: m.imageUrl
      }));

      const response = await fetch('/api/trade-advisor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get response');
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.message,
        citations: data.citations,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (err) {
      console.error('Trade advisor error:', err);
      setError(err instanceof Error ? err.message : 'Failed to get response');
    } finally {
      setIsLoading(false);
    }
  }, [input, imagePreview, messages, isLoading, clearImage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  const clearConversation = useCallback(() => {
    setMessages([]);
    setShowExamples(true);
  }, []);

  const useExamplePrompt = useCallback((prompt: string) => {
    setInput(prompt);
    textareaRef.current?.focus();
  }, []);

  // Loading state
  if (!isLoaded || userLoading) {
    return (
      <div className="min-h-screen bg-terminal-bg flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-terminal-dim">Loading Trade Advisor...</p>
        </div>
      </div>
    );
  }

  // Not signed in
  if (!isSignedIn) {
    return (
      <div className="min-h-screen bg-terminal-bg flex flex-col items-center justify-center p-4">
        <div className="max-w-md text-center">
          <Lock className="w-16 h-16 text-terminal-dim mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Sign In Required</h1>
          <p className="text-terminal-dim mb-6">
            Please sign in to access the Trade Advisor.
          </p>
          <div className="space-y-3">
            <Link
              to="/login"
              className="block w-full py-3 px-6 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black rounded-lg font-bold hover:opacity-90"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="block w-full py-3 px-6 bg-terminal-card border border-terminal-border text-white rounded-lg font-medium hover:bg-slate-800"
            >
              Create Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Check if user can access (has any tier except free)
  if (userData?.effectiveTier === 'free') {
    return (
      <div className="min-h-screen bg-terminal-bg flex flex-col items-center justify-center p-4">
        <div className="max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Subscription Required</h1>
          <p className="text-terminal-dim mb-6">
            Upgrade to access the AI Trade Advisor with 7-Step Framework analysis.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black rounded-lg font-bold hover:opacity-90"
          >
            <Sparkles className="w-5 h-5" />
            View Plans
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-terminal-bg flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-terminal-border bg-terminal-bg/95 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="relative">
                <div className="h-8 w-8 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded flex items-center justify-center text-black font-black text-xs">
                  7S
                </div>
              </div>
              <span className="text-terminal-bright font-bold tracking-tight text-lg">
                ALPHA<span className="text-cyan-400">OPTIONS</span>
              </span>
            </Link>

            <div className="flex items-center gap-4 text-sm">
              <span className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-lg text-purple-400">
                <Sparkles className="w-4 h-4" />
                Trade Advisor
              </span>
              <Link to="/dashboard" className="text-terminal-dim hover:text-white">
                Dashboard
              </Link>
              <Link to="/scanner" className="text-terminal-dim hover:text-white">
                Scanner
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Chat Area */}
      <main className="flex-1 flex flex-col max-w-5xl mx-auto w-full">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Welcome message if no messages */}
          {messages.length === 0 && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500/20 to-cyan-500/20 rounded-2xl mb-4">
                <MessageSquare className="w-8 h-8 text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">7-Step Trade Advisor</h2>
              <p className="text-terminal-dim max-w-lg mx-auto mb-6">
                Upload a screenshot of your position or describe your trade.
                I'll analyze it using the 7-Step Framework and give you actionable advice.
              </p>

              {/* Framework Steps Preview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto mb-8">
                <div className="p-3 bg-terminal-card rounded-lg border border-terminal-border">
                  <Target className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
                  <div className="text-sm text-terminal-dim">Context</div>
                </div>
                <div className="p-3 bg-terminal-card rounded-lg border border-terminal-border">
                  <TrendingUp className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                  <div className="text-sm text-terminal-dim">Trend</div>
                </div>
                <div className="p-3 bg-terminal-card rounded-lg border border-terminal-border">
                  <Shield className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                  <div className="text-sm text-terminal-dim">Risk</div>
                </div>
                <div className="p-3 bg-terminal-card rounded-lg border border-terminal-border">
                  <Clock className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                  <div className="text-sm text-terminal-dim">Execution</div>
                </div>
              </div>

              {/* Example prompts */}
              {showExamples && (
                <div className="space-y-2">
                  <p className="text-sm text-terminal-dim uppercase tracking-wider mb-3">Try asking:</p>
                  <div className="flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
                    {EXAMPLE_PROMPTS.slice(0, 4).map((prompt, i) => (
                      <button
                        key={i}
                        onClick={() => useExamplePrompt(prompt)}
                        className="px-3 py-1.5 text-sm bg-terminal-card border border-terminal-border rounded-full text-terminal-dim hover:text-white hover:border-cyan-500/50 transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Message List */}
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl p-4 ${
                  msg.role === 'user'
                    ? 'bg-cyan-500/10 border border-cyan-500/30'
                    : 'bg-terminal-card border border-terminal-border'
                }`}
              >
                {/* Image if present */}
                {msg.imageUrl && (
                  <div className="mb-3">
                    <img
                      src={msg.imageUrl}
                      alt="Position screenshot"
                      className="max-w-full max-h-64 rounded-lg border border-terminal-border"
                    />
                  </div>
                )}

                {/* Message content */}
                <div className={`prose prose-invert max-w-none ${msg.role === 'assistant' ? 'text-base' : ''}`}>
                  {msg.role === 'assistant' ? (
                    <div className="whitespace-pre-wrap text-terminal-text leading-relaxed">
                      {msg.content.split('\n').map((line, i) => {
                        // Style headers
                        if (line.startsWith('### ')) {
                          return <h3 key={i} className="text-lg font-bold text-white mt-4 mb-2">{line.replace('### ', '')}</h3>;
                        }
                        if (line.startsWith('## ')) {
                          return <h2 key={i} className="text-xl font-bold text-white mt-4 mb-2">{line.replace('## ', '')}</h2>;
                        }
                        // Style bold text
                        if (line.startsWith('**') && line.endsWith('**')) {
                          return <p key={i} className="font-bold text-white">{line.replace(/\*\*/g, '')}</p>;
                        }
                        // Style table rows
                        if (line.startsWith('|')) {
                          return <code key={i} className="block text-xs bg-slate-900 px-2 py-1 rounded">{line}</code>;
                        }
                        return <p key={i} className="mb-1">{line}</p>;
                      })}
                    </div>
                  ) : (
                    <p className="text-terminal-text">{msg.content}</p>
                  )}
                </div>

                {/* Citations if present */}
                {msg.citations && msg.citations.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-terminal-border">
                    <p className="text-xs text-terminal-dim mb-1">Sources:</p>
                    <div className="flex flex-wrap gap-1">
                      {msg.citations.slice(0, 3).map((citation, i) => (
                        <a
                          key={i}
                          href={citation}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-cyan-400 hover:underline truncate max-w-[200px]"
                        >
                          [{i + 1}]
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timestamp */}
                <div className="mt-2 text-xs text-terminal-dim">
                  {msg.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-terminal-card border border-terminal-border rounded-2xl p-4">
                <div className="flex items-center gap-2 text-terminal-dim">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Analyzing with 7-Step Framework...</span>
                </div>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex justify-center">
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-rose-400 text-sm">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                {error}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-terminal-border p-4 bg-terminal-bg/95 backdrop-blur-sm">
          {/* Image preview */}
          {imagePreview && (
            <div className="mb-3 relative inline-block">
              <img
                src={imagePreview}
                alt="Upload preview"
                className="max-h-32 rounded-lg border border-terminal-border"
              />
              <button
                onClick={clearImage}
                className="absolute -top-2 -right-2 p-1 bg-rose-500 rounded-full text-white hover:bg-rose-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="flex items-end gap-3">
            {/* Image upload button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-3 bg-terminal-card border border-terminal-border rounded-lg text-terminal-dim hover:text-white hover:border-cyan-500/50 transition-colors"
              title="Upload screenshot"
            >
              <ImageIcon className="w-5 h-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />

            {/* Text input */}
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe your position or ask about trade management..."
                className="w-full px-4 py-3 bg-terminal-card border border-terminal-border rounded-lg text-white placeholder-terminal-dim focus:border-cyan-500 focus:outline-none resize-none min-h-[48px] max-h-[200px]"
                rows={1}
              />
            </div>

            {/* Send button */}
            <button
              onClick={sendMessage}
              disabled={isLoading || (!input.trim() && !imagePreview)}
              className="p-3 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-lg text-black font-bold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Action bar */}
          <div className="flex items-center justify-between mt-3 text-sm text-terminal-dim">
            <div className="flex items-center gap-4">
              {messages.length > 0 && (
                <button
                  onClick={clearConversation}
                  className="flex items-center gap-1 hover:text-rose-400 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear chat
                </button>
              )}
              <button
                onClick={() => setShowExamples(!showExamples)}
                className="flex items-center gap-1 hover:text-white transition-colors"
              >
                {showExamples ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                Examples
              </button>
            </div>
            <div>
              Press Enter to send, Shift+Enter for new line
            </div>
          </div>

          {/* Collapsible examples */}
          {showExamples && messages.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {EXAMPLE_PROMPTS.map((prompt, i) => (
                <button
                  key={i}
                  onClick={() => useExamplePrompt(prompt)}
                  className="px-2 py-1 text-xs bg-terminal-card border border-terminal-border rounded text-terminal-dim hover:text-white hover:border-cyan-500/50 transition-colors"
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Disclaimer */}
      <footer className="border-t border-terminal-border p-3 text-center text-sm text-terminal-dim bg-terminal-bg">
        <p>
          This is educational analysis based on the 7-Step Framework. Not financial advice.
          Options trading involves substantial risk of loss.
        </p>
      </footer>
    </div>
  );
};

export default TradeAdvisorPage;
